import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {debounceTime, distinctUntilChanged, filter, map, startWith, takeUntil} from 'rxjs/operators';
import {combineLatest, Observable} from 'rxjs';
import {NbComponentSize, NbOptionComponent} from '@nebular/theme';

export interface MultiselectOption<T> {
    option: T;
    label: string;
}

export interface MultiselectItem {
    fieldGroup: string;
    disabled: boolean;
    isSelected: boolean;
}

@Component({
    selector: 'ngx-button-multiselect',
    templateUrl: './button-multiselect.component.html',
    styleUrls: ['./button-multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ButtonMultiselectComponent),
            multi: true,
        },
    ],
})
export class ButtonMultiselectComponent<
        T extends {
            fieldGroup: string;
            disabled: boolean;
            isSelected: boolean;
        },
    >
    extends Unsubscribable
    implements OnInit
{
    @Input() options$: Observable<MultiselectOption<T>[]>;
    @Input() defaultGroupTitle: string;
    @Input() iconDetails: {icon: string; pack?: string} = {icon: 'plus-outline'};
    @Input() btnTitle: string = '';
    @Input() status: string = 'basic';
    @Input() size: NbComponentSize = 'medium';
    @Output() optionChange: EventEmitter<{option: T; selected: boolean}> = new EventEmitter();

    @ViewChild('filterInput') filterInput: ElementRef;

    // Used to temporarily block focus from jumping to options when typing in the filter input
    private preventFocus: boolean = false;

    // Prevent Nebular from auto-focusing first visible option after filtering
    @HostListener('document:focusin', ['$event'])
    onFocusIn(event: FocusEvent) {
        if (this.preventFocus) {
            const el = event.target as HTMLElement;
            if (el?.tagName === 'NB-OPTION' || el?.closest('nb-option')) {
                event.stopPropagation();
                event.preventDefault();

                // Refocus input
                this.filterInput.nativeElement.focus();
            }
        }
    }

    options: MultiselectOption<T>[] = [];
    fieldGroups;

    _selectControl: FormControl = new FormControl([]);

    filterControl: FormControl = new FormControl();

    isButtonHover = false;
    disabled = false;

    filteredOptions$: Observable<MultiselectOption<T>[]>;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.options$.pipe(takeUntil(this.unsubscribe$)).subscribe((_options: MultiselectOption<T>[]) => {
            this.options = _options;
            this.fieldGroups = Array.from(
                new Set(this.options.map((item: MultiselectOption<T>) => item.option.fieldGroup)),
            );
        });

        this.filteredOptions$ = combineLatest([
            this.options$,
            this.filterControl.valueChanges.pipe(startWith('')),
        ]).pipe(
            map(([options, search]: [MultiselectOption<T>[], any]): MultiselectOption<T>[] => {
                const lowerSearch = search?.trim().toLowerCase?.() || '';

                const filtered = options.filter(
                    (optionItem) =>
                        !lowerSearch ||
                        optionItem.label.toLowerCase().includes(lowerSearch) ||
                        optionItem.option.isSelected,
                );

                if (!lowerSearch) {
                    return filtered;
                }

                return filtered.sort((a, b) => {
                    const aMatches = a.label.toLowerCase().includes(lowerSearch);
                    const bMatches = b.label.toLowerCase().includes(lowerSearch);

                    if (aMatches && !bMatches) return -1;
                    if (!aMatches && bMatches) return 1;

                    // If both match or both don't match, sort by isSelected
                    if (a.option.isSelected && !b.option.isSelected) return 1;
                    if (!a.option.isSelected && b.option.isSelected) return -1;

                    return 0; // keep original order otherwise
                });
            }),
        );

        this._selectControl.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter((value) => {
                    //when focusing on search input checkbox selects
                    //this removes search-input's value from this.selectControl
                    // and stops the process of subscription to this event
                    let isSearch = value?.includes('');
                    if (isSearch) {
                        let _value = value.filter((item) => item !== '');
                        this._selectControl.setValue(_value, {emitEvent: false});
                    }

                    return !isSearch;
                }),
                // Add or remove "select all" option (value = 0) based on current selection
                map((value: any[]) => {
                    let _value = value;
                    if (value && value.length === this.options.length && !value.includes(0)) {
                        _value = [...value, 0]; // Add "All" if everything else is selected
                        this._selectControl.setValue(_value, {emitEvent: false});
                    } else if (value && value.length !== this.options.length + 1) {
                        _value = value.filter((item) => item !== 0); // Remove "All" if anything is unselected
                        this._selectControl.setValue(_value, {emitEvent: false});
                    }
                    return _value;
                }),
                debounceTime(500),
            )
            .subscribe((value: any[]) => {
                this.emitValue(value);
            });
    }

    writeValue(value: any): void {
        if (value.length === this.options.length) value.push(0); // Add "All" if everything is selected
        this._selectControl.setValue(value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    toggleSelectAll() {
        if (this._selectControl.value.length >= this.options.length) {
            let _disabled: T[] = [];
            this.options.forEach((_option: MultiselectOption<T>) => {
                if (_option.option.disabled) _disabled.push(_option.option);
            });
            this._selectControl.reset(_disabled);
        } else {
            let _options: T[] = this.options.reduce((acc: T[], curr: MultiselectOption<T>) => {
                return [...acc, curr.option];
            }, []);
            this._selectControl.setValue([..._options, 0]);
        }
    }

    private onChange = (value: any) => {};

    private onTouched = () => {};

    private emitValue(value: any) {
        this.onChange(value.filter((item) => !!item));
        this.onTouched();
    }

    onSelectChange(nbOption: NbOptionComponent<T>) {
        nbOption.value.isSelected = !nbOption.value.isSelected;
        this.optionChange.emit({option: nbOption.value, selected: nbOption.selected});
    }

    // Handle input into search bar; manually manage focus to avoid unwanted jumps
    onInput() {
        this.preventFocus = true;

        setTimeout(() => {
            const input = this.filterInput.nativeElement;
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length); // Move cursor to end

            // Restore focus behavior (allow focus back to options) after this brief lock
            setTimeout(() => {
                this.preventFocus = false;
            }, 200); // Enough time to prevent Nebular/CDK focus
        });
    }
}
